/**
 * Seed inicial de datos para el mock mode.
 *
 * La idea: que cuando el usuario abra el demo, ya haya cosas para ver:
 *  - 2 objetivos activos con métricas reales
 *  - 1 objetivo en borrador
 *  - 3 segmentos guardados
 *  - 5 plantillas (4 aprobadas + 1 pendiente)
 *  - 4 notificaciones enviadas con engagement plausible
 *  - 2 campañas WhatsApp enviadas
 *  - 1 fila de brand rules con defaults
 */

export interface MockState {
    objectives: any[];
    segments: any[];
    templates: any[];
    notifications: any[];
    whatsappCampaigns: any[];
    whatsappAccounts: any[];
    brandRules: any | null;
    currentUser: { id: string; email: string; name: string } | null;
    inboxNotifications: InboxNotification[];
    assistantHistory: AssistantMessage[];
}

/**
 * Assistant chat — historial conversacional con la IA "Deenex".
 * Acumulado en memoria mientras dura la sesión (mock mode).
 */
export interface AssistantMessage {
    id: string;
    role: "user" | "assistant";
    content: string;
    /** Acciones contextuales que la IA propone (botones bajo el mensaje) */
    actions?: AssistantAction[];
    createdAt: string;
}

export interface AssistantAction {
    label: string;
    /** Ruta a la que navegar al click. Si tiene link, se renderiza como CTA */
    link?: string;
    /** Variante visual */
    variant?: "primary" | "secondary";
}

/**
 * Inbox notification — eventos del sistema que el admin recibe en el bell.
 * Distinto de `notifications` (que son push campaigns que el admin envía
 * a sus usuarios finales).
 */
export interface InboxNotification {
    id: string;
    type:
        | "campaign_sent"
        | "word_blocked"
        | "segment_growing"
        | "whatsapp_warmup"
        | "ban_risk"
        | "template_approved"
        | "ai_suggestion"
        | "system_update"
        | "frequency_cap"
        | "offhours_postponed"
        // ─── Modelo de 3 tipos de campaña ───
        | "campaign_suggested"
        | "campaign_needs_changes"
        | "campaign_approved"
        | "campaign_auto_paused";
    severity: "info" | "success" | "warning" | "critical";
    title: string;
    message: string;
    /** Ruta a la que linkea cuando se clickea (opcional) */
    link?: string;
    /** ISO timestamp */
    createdAt: string;
    read: boolean;
}

function id(prefix: string): string {
    return prefix + Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
}

function daysAgo(d: number): string {
    const date = new Date();
    date.setDate(date.getDate() - d);
    return date.toISOString();
}

function hoursAgo(h: number): string {
    const date = new Date();
    date.setHours(date.getHours() - h);
    return date.toISOString();
}

function minutesAgo(m: number): string {
    const date = new Date();
    date.setMinutes(date.getMinutes() - m);
    return date.toISOString();
}

export function seedData(): MockState {
    // IDs estáticos para las entidades del seed inicial.
    // Esto es lo que permite que /objectives/:id y /segments/:id sigan
    // funcionando al recargar la página (los IDs dinámicos se regeneraban
    // en cada init del mock store, rompiendo los deep-links).
    const segId1 = "seg-vip";
    const segId2 = "seg-carritos-24h";
    const segId3 = "seg-inactivos-30d";
    const segId4 = "seg-primera-vez";
    const segId5 = "seg-belgrano-inactivos";
    const segId6 = "seg-ticket-bajo";
    const segId7 = "seg-cumple-mes";

    const tplId1 = "tpl-promo-descuento";
    const tplId2 = "tpl-carrito-abandonado";
    const tplId3 = "tpl-reactivacion-inactivos";
    const tplId4 = "tpl-feliz-cumple";
    const tplId5 = "tpl-promo-invierno";

    // Efemérides
    const objId1 = "obj-workers-day";
    const objId2 = "obj-fathers-day";
    const objId3 = "obj-friends-day";
    const objId4 = "obj-kids-day";
    const objId5 = "obj-mothers-day";
    const objId6 = "obj-christmas-newyear";
    const objId7 = "obj-valentines";
    const objId8 = "obj-birthday";
    const objId9 = "obj-customer-anniversary";
    // Comportamentales
    const objId10 = "obj-cart-abandon";
    const objId11 = "obj-winback-vip";
    const objId12 = "obj-purchase-frequency";
    const objId13 = "obj-aov-increase";
    const objId14 = "obj-welcome-first";
    const objId15 = "obj-winback-general";
    const objId16 = "obj-cross-sell";
    const objId17 = "obj-reorder-reminder";
    const objId18 = "obj-review-request";
    // Custom (demo)
    const objId19 = "obj-custom-demo";

    const segments = [
        {
            id: segId1,
            brandId: "brand-demo",
            name: "Clientes VIP",
            emoji: "💎",
            createdBy: "demo-user",
            filter: { totalSpent: { $gte: 30000 }, totalOrders: { $gte: 5 } },
            filteredFields: ["totalSpent", "totalOrders"],
            status: "saved",
            conversationSummary:
                "Clientes que compraron más de 5 veces en los últimos 90 días con LTV mayor a $30k.",
            lastKnownCount: 936,
            conversationHistory: [],
            customDefinitions: {},
            currentUndoIndex: 0,
            lastUsedAt: hoursAgo(3),
            createdAt: daysAgo(45),
            updatedAt: daysAgo(10),
        },
        {
            id: segId2,
            brandId: "brand-demo",
            name: "Carritos abandonados 24h",
            emoji: "🛒",
            createdBy: "demo-user",
            filter: { cartAbandonedAt: { $gte: "today-24h" }, cartValue: { $gt: 1500 } },
            filteredFields: ["cartAbandonedAt", "cartValue"],
            status: "saved",
            conversationSummary:
                "Usuarios que dejaron carrito abandonado en las últimas 24h con valor mayor a $1.500.",
            lastKnownCount: 218,
            conversationHistory: [],
            customDefinitions: {},
            currentUndoIndex: 0,
            lastUsedAt: hoursAgo(1),
            createdAt: daysAgo(30),
            updatedAt: daysAgo(2),
        },
        {
            id: segId3,
            brandId: "brand-demo",
            name: "Inactivos 30 días",
            emoji: "😴",
            createdBy: "demo-user",
            filter: { daysSinceLastOrder: { $gte: 30 } },
            filteredFields: ["daysSinceLastOrder"],
            status: "saved",
            conversationSummary:
                "Clientes que no compraron en los últimos 30 días pero que sí pidieron antes.",
            lastKnownCount: 1542,
            conversationHistory: [],
            customDefinitions: {},
            currentUndoIndex: 0,
            lastUsedAt: daysAgo(5),
            createdAt: daysAgo(20),
            updatedAt: daysAgo(8),
        },
        {
            id: segId4,
            brandId: "brand-demo",
            name: "Compradores primera vez",
            emoji: "👋",
            createdBy: "demo-user",
            filter: { totalOrders: 1 },
            filteredFields: ["totalOrders"],
            status: "saved",
            conversationSummary:
                "Usuarios que ya hicieron su primera compra y todavía no la segunda.",
            lastKnownCount: 2148,
            conversationHistory: [],
            customDefinitions: {},
            currentUndoIndex: 0,
            lastUsedAt: hoursAgo(8),
            createdAt: daysAgo(60),
            updatedAt: daysAgo(15),
        },
        {
            id: segId5,
            brandId: "brand-demo",
            name: "Belgrano · 45+ días inactivos",
            emoji: "📍",
            createdBy: "demo-user",
            filter: { localId: "loc-2", daysSinceLastOrder: { $gte: 45 } },
            filteredFields: ["localId", "daysSinceLastOrder"],
            status: "saved",
            conversationSummary:
                "Clientes de la sucursal Belgrano que no piden hace más de 45 días.",
            lastKnownCount: 312,
            conversationHistory: [],
            customDefinitions: {},
            currentUndoIndex: 0,
            lastUsedAt: daysAgo(7),
            createdAt: daysAgo(28),
            updatedAt: daysAgo(7),
        },
        {
            id: segId6,
            brandId: "brand-demo",
            name: "Ticket bajo · empuje upsell",
            emoji: "💸",
            createdBy: "demo-user",
            filter: { avgTicket: { $lt: 3000 } },
            filteredFields: ["avgTicket"],
            status: "saved",
            conversationSummary:
                "Clientes con ticket promedio menor a $3.000 — candidatos para upsell.",
            lastKnownCount: 1248,
            conversationHistory: [],
            customDefinitions: {},
            currentUndoIndex: 0,
            lastUsedAt: hoursAgo(6),
            createdAt: daysAgo(40),
            updatedAt: daysAgo(10),
        },
        {
            id: segId7,
            brandId: "brand-demo",
            name: "Cumpleañeros este mes",
            emoji: "🎂",
            createdBy: "demo-user",
            filter: { birthMonth: "current" },
            filteredFields: ["birthMonth"],
            status: "draft",
            conversationSummary:
                "Clientes que cumplen años este mes — para campañas de feliz cumpleaños.",
            lastKnownCount: 87,
            conversationHistory: [],
            customDefinitions: {},
            currentUndoIndex: 0,
            createdAt: daysAgo(3),
            updatedAt: daysAgo(3),
        },
    ];

    /* ─── Plantillas ─── */
    const templates = [
        {
            id: tplId1,
            brandId: "brand-demo",
            name: "promo_descuento",
            category: "marketing",
            language: "es_AR",
            approvalStatus: "approved",
            header: { type: "image", variableCount: 0 },
            body:
                "Hola {{1}}, tenemos una oferta especial para vos 🎉\n\n*{{2}}% OFF* en toda la tienda usando el código *{{3}}*\n\nVálido hasta fin de mes.",
            variableCount: 3,
            footer: "Si no querés recibir más promos, escribí BAJA.",
            buttons: [
                { type: "url", label: "Comprar ahora", url: "https://tienda.deenex.com/" },
                { type: "quick_reply", label: "Más info" },
            ],
            estimatedCostUSD: 0.0573,
            generatedByAI: false,
            createdAt: daysAgo(60),
            updatedAt: daysAgo(60),
        },
        {
            id: tplId2,
            brandId: "brand-demo",
            name: "carrito_abandonado",
            category: "marketing",
            language: "es_AR",
            approvalStatus: "approved",
            body:
                "Hola {{1}} 👋\n\nDejaste *{{2}} producto/s* en tu carrito. ¿Querés terminar tu compra?\n\nTotal: ${{3}}",
            variableCount: 3,
            buttons: [
                { type: "url", label: "Volver al carrito", url: "https://tienda.deenex.com/cart" },
                { type: "quick_reply", label: "Hablar con asesor" },
            ],
            estimatedCostUSD: 0.0573,
            generatedByAI: false,
            createdAt: daysAgo(50),
            updatedAt: daysAgo(50),
        },
        {
            id: tplId3,
            brandId: "brand-demo",
            name: "reactivacion_inactivos",
            category: "marketing",
            language: "es_AR",
            approvalStatus: "approved",
            body:
                "Hola {{1}}, te extrañamos 😢\n\nVolvé y disfrutá *{{2}}% OFF* en tu próximo pedido.\n\nVálido por 7 días.",
            variableCount: 2,
            buttons: [{ type: "quick_reply", label: "¡Me sumo!" }],
            estimatedCostUSD: 0.0573,
            generatedByAI: false,
            createdAt: daysAgo(40),
            updatedAt: daysAgo(40),
        },
        {
            id: tplId4,
            brandId: "brand-demo",
            name: "feliz_cumple",
            category: "marketing",
            language: "es_AR",
            approvalStatus: "approved",
            header: { type: "text", text: "🎂 ¡Feliz cumple, {{1}}!", variableCount: 1 },
            body:
                "Para festejarlo, te regalamos *{{1}}% OFF* en tu próxima compra.\n\nUsá el código *{{2}}* hasta el {{3}}.",
            variableCount: 3,
            buttons: [{ type: "url", label: "Usar regalo", url: "https://tienda.deenex.com/" }],
            estimatedCostUSD: 0.0573,
            generatedByAI: false,
            createdAt: daysAgo(20),
            updatedAt: daysAgo(20),
        },
        {
            id: tplId5,
            brandId: "brand-demo",
            name: "promo_invierno",
            category: "marketing",
            language: "es_AR",
            approvalStatus: "pending",
            body:
                "Hola {{1}}, llegó el frío y nuestras camperas tienen *{{2}}% OFF*. Sumate ya.",
            variableCount: 2,
            buttons: [],
            estimatedCostUSD: 0.0573,
            generatedByAI: true,
            createdAt: daysAgo(2),
            updatedAt: daysAgo(2),
        },
    ];

    /* ─── Campañas (9 presets de efemérides + recurrentes personales) ─── */
    const objectives = [
        /* 1. Día del Trabajador — active, ya disparada (1 mayo pasó hace ~20 días) */
        {
            id: objId1,
            brandId: "brand-demo",
            name: "Día del Trabajador 2026",
            emoji: "🛠️",
            preset: "workers_day",
            segmentId: segId1,
            templateId: tplId3,
            setupMode: "auto",
            cadence: {
                kind: "one_shot",
                sendWindow: { start: "10:00", end: "21:00" },
                scheduledFor: daysAgo(20),
            },
            status: "active",
            metrics: {
                runsCount: 1,
                peopleReached: 1480,
                conversions: 198,
                revenueGeneratedUSD: 9420,
                lastRunAt: daysAgo(20),
            },
            createdBy: "demo-user",
            createdAt: daysAgo(45),
            updatedAt: daysAgo(20),
        },
        /* 2. Día del Padre — active, próxima (junio) */
        {
            id: objId2,
            brandId: "brand-demo",
            name: "Día del Padre 2026",
            emoji: "👨",
            preset: "fathers_day",
            segmentId: segId1,
            templateId: tplId2,
            setupMode: "auto",
            cadence: {
                kind: "one_shot",
                sendWindow: { start: "10:00", end: "21:00" },
                scheduledFor: daysAgo(-25),
            },
            status: "active",
            metrics: {
                runsCount: 0,
                peopleReached: 0,
                conversions: 0,
                revenueGeneratedUSD: 0,
            },
            createdBy: "demo-user",
            createdAt: daysAgo(7),
            updatedAt: daysAgo(2),
        },
        /* 3. Día del Amigo — draft, julio */
        {
            id: objId3,
            brandId: "brand-demo",
            name: "Día del Amigo — Mesas grandes",
            emoji: "🍻",
            preset: "friends_day",
            cadence: {
                kind: "one_shot",
                sendWindow: { start: "11:00", end: "22:00" },
                scheduledFor: daysAgo(-60),
            },
            status: "draft",
            metrics: {
                runsCount: 0,
                peopleReached: 0,
                conversions: 0,
                revenueGeneratedUSD: 0,
            },
            createdBy: "demo-user",
            createdAt: daysAgo(2),
            updatedAt: daysAgo(2),
        },
        /* 4. Día del Niño — draft, agosto */
        {
            id: objId4,
            brandId: "brand-demo",
            name: "Día del Niño — Menú infantil",
            emoji: "🧒",
            preset: "kids_day",
            cadence: {
                kind: "one_shot",
                sendWindow: { start: "11:00", end: "20:00" },
                scheduledFor: daysAgo(-95),
            },
            status: "draft",
            metrics: {
                runsCount: 0,
                peopleReached: 0,
                conversions: 0,
                revenueGeneratedUSD: 0,
            },
            createdBy: "demo-user",
            createdAt: daysAgo(2),
            updatedAt: daysAgo(2),
        },
        /* 5. Día de la Madre — draft, octubre */
        {
            id: objId5,
            brandId: "brand-demo",
            name: "Día de la Madre — Almuerzo familiar",
            emoji: "👩",
            preset: "mothers_day",
            cadence: {
                kind: "one_shot",
                sendWindow: { start: "10:00", end: "21:00" },
                scheduledFor: daysAgo(-155),
            },
            status: "draft",
            metrics: {
                runsCount: 0,
                peopleReached: 0,
                conversions: 0,
                revenueGeneratedUSD: 0,
            },
            createdBy: "demo-user",
            createdAt: daysAgo(2),
            updatedAt: daysAgo(2),
        },
        /* 6. Navidad y Año Nuevo — draft, diciembre */
        {
            id: objId6,
            brandId: "brand-demo",
            name: "Navidad y Año Nuevo 2026",
            emoji: "🎄",
            preset: "christmas_newyear",
            cadence: {
                kind: "one_shot",
                sendWindow: { start: "10:00", end: "22:00" },
                scheduledFor: daysAgo(-215),
            },
            status: "draft",
            metrics: {
                runsCount: 0,
                peopleReached: 0,
                conversions: 0,
                revenueGeneratedUSD: 0,
            },
            createdBy: "demo-user",
            createdAt: daysAgo(1),
            updatedAt: daysAgo(1),
        },
        /* 7. Día de los Enamorados — paused, ya pasó (febrero) con métricas */
        {
            id: objId7,
            brandId: "brand-demo",
            name: "Día de los Enamorados 2026",
            emoji: "💝",
            preset: "valentines",
            segmentId: segId2,
            templateId: tplId4,
            setupMode: "manual",
            cadence: {
                kind: "one_shot",
                sendWindow: { start: "11:00", end: "21:00" },
                scheduledFor: daysAgo(95),
            },
            status: "paused",
            metrics: {
                runsCount: 1,
                peopleReached: 2148,
                conversions: 312,
                revenueGeneratedUSD: 18720,
                lastRunAt: daysAgo(95),
            },
            createdBy: "demo-user",
            createdAt: daysAgo(120),
            updatedAt: daysAgo(95),
        },
        /* 8. Feliz Cumpleaños — active, recurrente todo el año */
        {
            id: objId8,
            brandId: "brand-demo",
            name: "Feliz cumpleaños",
            emoji: "🎂",
            preset: "birthday",
            segmentId: segId1,
            templateId: tplId3,
            setupMode: "auto",
            cadence: {
                kind: "recurring",
                sendWindow: { start: "10:00", end: "20:00" },
                daysOfWeek: [1, 2, 3, 4, 5, 6, 7],
                triggerCondition: "user.birthDate === today",
            },
            status: "active",
            metrics: {
                runsCount: 47,
                peopleReached: 1247,
                conversions: 762,
                revenueGeneratedUSD: 28640,
                lastRunAt: hoursAgo(8),
            },
            createdBy: "demo-user",
            createdAt: daysAgo(90),
            updatedAt: daysAgo(15),
        },
        /* 9. Aniversario del cliente — active, recurrente */
        {
            id: objId9,
            brandId: "brand-demo",
            name: "Aniversario como cliente",
            emoji: "🎉",
            preset: "customer_anniversary",
            segmentId: segId1,
            templateId: tplId2,
            setupMode: "auto",
            cadence: {
                kind: "recurring",
                sendWindow: { start: "11:00", end: "19:00" },
                daysOfWeek: [1, 2, 3, 4, 5, 6, 7],
                triggerCondition: "user.firstPurchaseDate.anniversary === today",
            },
            status: "active",
            metrics: {
                runsCount: 23,
                peopleReached: 568,
                conversions: 184,
                revenueGeneratedUSD: 8920,
                lastRunAt: hoursAgo(36),
            },
            createdBy: "demo-user",
            createdAt: daysAgo(60),
            updatedAt: daysAgo(20),
        },
        /* ───────────────── Comportamentales ───────────────── */
        /* 10. Carrito abandonado — active, alta conversión */
        {
            id: objId10,
            brandId: "brand-demo",
            name: "Carrito abandonado · 2hs",
            emoji: "🛒",
            preset: "cart_abandon",
            segmentId: segId2,
            templateId: tplId2,
            setupMode: "auto",
            cadence: {
                kind: "recurring",
                sendWindow: { start: "11:00", end: "22:00" },
                daysOfWeek: [1, 2, 3, 4, 5, 6, 7],
                triggerCondition: "cart.abandoned > 2h && cart.value > 1500",
            },
            status: "active",
            metrics: {
                runsCount: 124,
                peopleReached: 3892,
                conversions: 712,
                revenueGeneratedUSD: 38450,
                lastRunAt: hoursAgo(1),
            },
            createdBy: "demo-user",
            createdAt: daysAgo(75),
            updatedAt: daysAgo(5),
        },
        /* 11. Reactivar VIP — active */
        {
            id: objId11,
            brandId: "brand-demo",
            name: "Reactivar VIP — 30+ días",
            emoji: "🔁",
            preset: "winback_vip",
            segmentId: segId1,
            templateId: tplId3,
            setupMode: "auto",
            cadence: {
                kind: "recurring",
                sendWindow: { start: "10:00", end: "20:00" },
                daysOfWeek: [2, 3, 4, 5],
                triggerCondition: "user.ltv > 30000 && user.daysSinceLastOrder > 30",
            },
            status: "active",
            metrics: {
                runsCount: 18,
                peopleReached: 412,
                conversions: 184,
                revenueGeneratedUSD: 22480,
                lastRunAt: hoursAgo(12),
            },
            createdBy: "demo-user",
            createdAt: daysAgo(50),
            updatedAt: daysAgo(8),
        },
        /* 12. Aumentar frecuencia — paused (en pruebas) */
        {
            id: objId12,
            brandId: "brand-demo",
            name: "Subir frecuencia 1-3 pedidos",
            emoji: "📈",
            preset: "purchase_frequency",
            segmentId: segId2,
            templateId: tplId2,
            setupMode: "auto",
            cadence: {
                kind: "recurring",
                sendWindow: { start: "12:00", end: "20:00" },
                daysOfWeek: [4, 5, 6],
                triggerCondition: "user.totalOrders >= 1 && user.totalOrders <= 3",
            },
            status: "paused",
            metrics: {
                runsCount: 6,
                peopleReached: 540,
                conversions: 89,
                revenueGeneratedUSD: 4280,
                lastRunAt: daysAgo(5),
            },
            createdBy: "demo-user",
            createdAt: daysAgo(28),
            updatedAt: daysAgo(5),
        },
        /* 13. Aumentar ticket promedio — active */
        {
            id: objId13,
            brandId: "brand-demo",
            name: "Subir ticket promedio",
            emoji: "💸",
            preset: "aov_increase",
            segmentId: segId1,
            templateId: tplId4,
            setupMode: "auto",
            cadence: {
                kind: "recurring",
                sendWindow: { start: "11:00", end: "22:00" },
                daysOfWeek: [1, 2, 3, 4, 5, 6, 7],
                triggerCondition: "user.avgTicket < brand.avgTicket * 0.8",
            },
            status: "active",
            metrics: {
                runsCount: 35,
                peopleReached: 1248,
                conversions: 198,
                revenueGeneratedUSD: 11760,
                lastRunAt: hoursAgo(6),
            },
            createdBy: "demo-user",
            createdAt: daysAgo(40),
            updatedAt: daysAgo(10),
        },
        /* 14. Bienvenida primera compra — active, alta conversión */
        {
            id: objId14,
            brandId: "brand-demo",
            name: "Bienvenida primera compra",
            emoji: "👋",
            preset: "welcome_first",
            segmentId: segId2,
            templateId: tplId3,
            setupMode: "auto",
            cadence: {
                kind: "recurring",
                sendWindow: { start: "11:00", end: "22:00" },
                daysOfWeek: [1, 2, 3, 4, 5, 6, 7],
                triggerCondition: "user.totalOrders === 1 && order.justDelivered",
            },
            status: "active",
            metrics: {
                runsCount: 89,
                peopleReached: 2154,
                conversions: 1442,
                revenueGeneratedUSD: 31280,
                lastRunAt: hoursAgo(2),
            },
            createdBy: "demo-user",
            createdAt: daysAgo(90),
            updatedAt: daysAgo(12),
        },
        /* 15. Despertar inactivos — always-on activa (corrige estado inconsistente) */
        {
            id: objId15,
            brandId: "brand-demo",
            name: "Despertar inactivos 60+ días",
            emoji: "😴",
            preset: "winback_general",
            cadence: {
                kind: "recurring",
                sendWindow: { start: "11:00", end: "19:00" },
                daysOfWeek: [2, 4],
                triggerCondition: "user.daysSinceLastOrder > 60",
            },
            status: "active",
            metrics: {
                runsCount: 18,
                peopleReached: 824,
                conversions: 142,
                revenueGeneratedUSD: 6820,
                lastRunAt: hoursAgo(6),
            },
            createdBy: "demo-user",
            createdAt: daysAgo(60),
            updatedAt: hoursAgo(6),
        },
        /* 16. Cross-sell — active */
        {
            id: objId16,
            brandId: "brand-demo",
            name: "Cross-sell 3 días post pedido",
            emoji: "🛍️",
            preset: "cross_sell",
            segmentId: segId1,
            templateId: tplId2,
            setupMode: "auto",
            cadence: {
                kind: "recurring",
                sendWindow: { start: "12:00", end: "20:00" },
                daysOfWeek: [1, 2, 3, 4, 5, 6, 7],
                triggerCondition: "order.deliveredAt + 3d",
            },
            status: "active",
            metrics: {
                runsCount: 62,
                peopleReached: 1873,
                conversions: 248,
                revenueGeneratedUSD: 9420,
                lastRunAt: hoursAgo(4),
            },
            createdBy: "demo-user",
            createdAt: daysAgo(55),
            updatedAt: daysAgo(14),
        },
        /* 17. Próximo pedido estimado — active (la AI feature más fuerte) */
        {
            id: objId17,
            brandId: "brand-demo",
            name: "Próximo pedido estimado · AI",
            emoji: "⏰",
            preset: "reorder_reminder",
            segmentId: segId1,
            templateId: tplId3,
            setupMode: "auto",
            cadence: {
                kind: "recurring",
                sendWindow: { start: "11:00", end: "21:00" },
                daysOfWeek: [1, 2, 3, 4, 5, 6, 7],
                triggerCondition: "ai.predictedNextOrder - 1d",
            },
            status: "active",
            metrics: {
                runsCount: 41,
                peopleReached: 982,
                conversions: 287,
                revenueGeneratedUSD: 14920,
                lastRunAt: hoursAgo(3),
            },
            createdBy: "demo-user",
            createdAt: daysAgo(35),
            updatedAt: daysAgo(7),
        },
        /* 18. Pedir review — active */
        {
            id: objId18,
            brandId: "brand-demo",
            name: "Pedir review · Google Maps",
            emoji: "⭐",
            preset: "review_request",
            segmentId: segId1,
            templateId: tplId4,
            setupMode: "auto",
            cadence: {
                kind: "recurring",
                sendWindow: { start: "11:00", end: "20:00" },
                daysOfWeek: [1, 2, 3, 4, 5, 6, 7],
                triggerCondition: "order.deliveredAt + 2d && order.rating !== negative",
            },
            status: "active",
            metrics: {
                runsCount: 78,
                peopleReached: 2418,
                conversions: 412,
                revenueGeneratedUSD: 0,
                lastRunAt: hoursAgo(7),
            },
            createdBy: "demo-user",
            createdAt: daysAgo(65),
            updatedAt: daysAgo(20),
        },
        /* 19. Custom — ejemplo de campaña personalizada con métricas a levantar */
        {
            id: objId19,
            brandId: "brand-demo",
            name: "Reactivar Belgrano · Miércoles flojos",
            emoji: "🎯",
            preset: "custom",
            segmentId: segId3,
            templateId: tplId2,
            setupMode: "manual",
            cadence: {
                kind: "recurring",
                sendWindow: { start: "11:00", end: "18:00" },
                daysOfWeek: [3],
                triggerCondition: "user.localId === 'loc-2' && user.daysSinceLastOrder > 45",
            },
            status: "active",
            customGoal:
                "Contactar a clientes de la sucursal Belgrano que no pidieron hace más de 45 días, con una promo de envío gratis los miércoles para llenar la cocina en el día más flojo de la semana.",
            customMetrics: ["orders_count", "purchase_frequency", "retention"],
            metrics: {
                runsCount: 4,
                peopleReached: 312,
                conversions: 67,
                revenueGeneratedUSD: 3840,
                lastRunAt: daysAgo(7),
            },
            createdBy: "demo-user",
            createdAt: daysAgo(28),
            updatedAt: daysAgo(7),
        },
    ];

    /* ───────────────────────────────────────────────────────────────
     *  Modelo de 3 tipos — decoración + campañas nuevas
     * ───────────────────────────────────────────────────────────────
     *
     *  Cada campaña existente recibe un `origin` derivado de su preset:
     *    - Lifecycle/comportamentales → automatic + always-on
     *    - Efemérides/seasonal activas o pausadas por la marca → custom
     *    - El preset "custom" siempre es custom
     *
     *  Luego apendamos campañas nuevas que muestran los estados nuevos
     *  (suggested, dismissed, draft, pending_review, needs_changes).
     */

    // Lifecycle/comportamentales — Deenex las prende automáticamente
    const AUTOMATIC_PRESETS = new Set([
        "cart_abandon",
        "winback_general",
        "winback_vip",
        "cross_sell",
        "welcome_first",
        "birthday",
        "customer_anniversary",
        "reorder_reminder",
        "review_request",
        "purchase_frequency",
        "aov_increase",
    ]);
    // Las always-on no pueden eliminarse, solo pausarse
    const ALWAYS_ON_PRESETS = new Set([
        "cart_abandon",
        "winback_general",
        "welcome_first",
        "birthday",
        "cross_sell",
        "reorder_reminder",
    ]);

    objectives.forEach((o: any) => {
        if (o.preset === "custom") {
            o.origin = "custom";
        } else if (AUTOMATIC_PRESETS.has(o.preset)) {
            o.origin = "automatic";
            if (ALWAYS_ON_PRESETS.has(o.preset)) {
                o.isAlwaysOn = true;
            }
        } else {
            // Efemérides activadas por la marca
            o.origin = "custom";
        }
        // Para las custom activas/pausadas, hacemos que tengan un review aprobado en su trail
        if (o.origin === "custom" && (o.status === "active" || o.status === "paused")) {
            o.aiReview = {
                iteration: 1,
                lastReviewedAt: o.createdAt,
                verdict: "approved",
                observations: [],
                approvedAt: o.createdAt,
                approverNote:
                    "Revisada y aprobada por la IA. Cumple con tu manual de marca, no choca con otra campaña activa y respeta tu ventana horaria.",
            };
        }
    });

    /* ─── Sugeridas (Tipo 2) — efemérides próximas + oportunidades ─── */
    const sugId1 = id("obj-");
    const sugId2 = id("obj-");
    const sugId3 = id("obj-");
    const sugId4 = id("obj-");

    const suggestedCampaigns: any[] = [
        {
            id: sugId1,
            brandId: "brand-demo",
            name: "Día del Padre · Reservas anticipadas",
            emoji: "👨",
            preset: "fathers_day",
            origin: "suggested",
            segmentId: segId1,
            cadence: {
                kind: "one_shot",
                sendWindow: { start: "11:00", end: "20:00" },
                scheduledFor: daysAgo(-22),
            },
            status: "suggested",
            metrics: {
                runsCount: 0,
                peopleReached: 0,
                conversions: 0,
                revenueGeneratedUSD: 0,
            },
            suggestionReason:
                "Falta menos de 1 mes para el Día del Padre (3er domingo de junio). Restaurantes similares duplican reservas activando esta campaña con 3 semanas de anticipación.",
            suggestionExpiresAt: daysAgo(-15),
            messagePreview:
                "Hola {{nombre}} 👨‍👦 ¿Ya pensaste qué hacer para el Día del Padre? Reservá tu mesa con 3 semanas de anticipación y te regalamos un postre para compartir. Link de reserva: deenex.com.ar/reservas",
            createdBy: "ai-deenex",
            createdAt: hoursAgo(8),
            updatedAt: hoursAgo(8),
        },
        {
            id: sugId2,
            brandId: "brand-demo",
            name: "Reactivar VIPs que están enfriándose",
            emoji: "🥶",
            preset: "winback_vip",
            origin: "suggested",
            segmentId: segId2,
            cadence: {
                kind: "one_shot",
                sendWindow: { start: "12:00", end: "21:00" },
            },
            status: "suggested",
            metrics: {
                runsCount: 0,
                peopleReached: 0,
                conversions: 0,
                revenueGeneratedUSD: 0,
            },
            suggestionReason:
                "Detecté 38 clientes VIP (top 20% LTV) que están a punto de cruzar la barrera de 30 días sin pedir. Si los reactivás ahora, evitás que pasen al pool de 'inactivos genéricos' donde son más caros de recuperar.",
            suggestionExpiresAt: daysAgo(-5),
            messagePreview:
                "Hola {{nombre}}, te extrañamos 💜 Hace casi un mes que no pasás. Te dejamos un cupón de envío gratis + 15% off, válido los próximos 5 días.",
            createdBy: "ai-deenex",
            createdAt: hoursAgo(14),
            updatedAt: hoursAgo(14),
        },
        {
            id: sugId3,
            brandId: "brand-demo",
            name: "Lanzamiento: Pizzas de autor",
            emoji: "🍕",
            preset: "cross_sell",
            origin: "suggested",
            segmentId: segId4,
            cadence: {
                kind: "one_shot",
                sendWindow: { start: "18:00", end: "22:00" },
            },
            status: "suggested",
            metrics: {
                runsCount: 0,
                peopleReached: 0,
                conversions: 0,
                revenueGeneratedUSD: 0,
            },
            suggestionReason:
                "Vi que cargaste 3 productos nuevos esta semana ('Pizza Trufa', 'Pizza Burrata', 'Pizza Marinara'). Te recomiendo lanzarlos a clientes que ya pidieron pizza antes — son los más propensos a probar.",
            suggestionExpiresAt: daysAgo(-7),
            messagePreview:
                "¡Tenemos novedades para vos {{nombre}}! 🍕 3 pizzas de autor recién armadas: trufa, burrata y marinara. Probalas esta semana con 20% off + un postre de regalo.",
            createdBy: "ai-deenex",
            createdAt: hoursAgo(28),
            updatedAt: hoursAgo(28),
        },
        // Una sugerencia ya descartada — para mostrar el estado dismissed
        {
            id: sugId4,
            brandId: "brand-demo",
            name: "Domingo a la noche · Mesas vacías",
            emoji: "🌙",
            preset: "custom",
            origin: "suggested",
            segmentId: segId5,
            cadence: {
                kind: "recurring",
                sendWindow: { start: "16:00", end: "20:00" },
                daysOfWeek: [7],
            },
            status: "dismissed",
            dismissReason: "No me convence el copy",
            metrics: {
                runsCount: 0,
                peopleReached: 0,
                conversions: 0,
                revenueGeneratedUSD: 0,
            },
            suggestionReason:
                "Los últimos 4 domingos a la noche tuviste 30% menos pedidos vs promedio. Te recomiendo una campaña dominical con promo 2x1 para llenar la cocina.",
            messagePreview:
                "Domingo de relax 🌙 Pedí en pareja: 2 platos + 1 postre por $X. Solo hoy.",
            createdBy: "ai-deenex",
            createdAt: daysAgo(2),
            updatedAt: daysAgo(1),
        },
    ];

    /* ─── Custom en distintos estados de revisión IA (Tipo 3) ─── */
    const cusId1 = id("obj-");
    const cusId2 = id("obj-");
    const cusId3 = id("obj-");

    const customInReview: any[] = [
        // Borrador — la marca todavía editando
        {
            id: cusId1,
            brandId: "brand-demo",
            name: "Combo familiar fin de semana",
            emoji: "👨‍👩‍👧",
            preset: "custom",
            origin: "custom",
            segmentId: segId6,
            cadence: {
                kind: "recurring",
                sendWindow: { start: "10:00", end: "13:00" },
                daysOfWeek: [6, 7],
            },
            status: "draft",
            metrics: {
                runsCount: 0,
                peopleReached: 0,
                conversions: 0,
                revenueGeneratedUSD: 0,
            },
            customGoal:
                "Lanzar un combo familiar (4 platos + bebidas) para los fines de semana, apuntando a familias que históricamente piden poco volumen.",
            customMetrics: ["avg_ticket", "revenue"],
            messagePreview:
                "Sábado en familia 👨‍👩‍👧 Combo familiar: 4 platos + 4 bebidas con 25% off. Pedilo hoy o mañana.",
            createdBy: "demo-user",
            createdAt: hoursAgo(3),
            updatedAt: hoursAgo(1),
        },
        // Necesita ajustes — la IA observó cosas
        {
            id: cusId2,
            brandId: "brand-demo",
            name: "Promo agresiva 50% off · Inactivos",
            emoji: "🔥",
            preset: "custom",
            origin: "custom",
            segmentId: segId3,
            cadence: {
                kind: "one_shot",
                sendWindow: { start: "09:00", end: "23:00" },
            },
            status: "needs_changes",
            metrics: {
                runsCount: 0,
                peopleReached: 0,
                conversions: 0,
                revenueGeneratedUSD: 0,
            },
            customGoal:
                "Recuperar agresivo a los inactivos 30+ días con 50% off de bienvenida de vuelta.",
            customMetrics: ["retention", "orders_count"],
            messagePreview:
                "¡Te extrañamos! 🔥 50% OFF en TODO + envío 100% gratis. Promo única, garantizada. ¡No te lo pierdas!",
            aiReview: {
                iteration: 1,
                lastReviewedAt: hoursAgo(2),
                verdict: "needs_changes",
                observations: [
                    {
                        category: "tone_mismatch",
                        severity: "warning",
                        message:
                            "Tu manual de marca tiene tono 65 (neutral). Este copy suena en 85 (muy agresivo). Las palabras '¡No te lo pierdas!' y los signos de exclamación duplicados rompen tu voz.",
                        suggestion:
                            "Probá: 'Hace tiempo que no nos vemos. Te dejamos 50% off y envío gratis, válido los próximos 5 días.'",
                    },
                    {
                        category: "spam_risk",
                        severity: "critical",
                        message:
                            "Detecté '100% gratis', 'garantizada' y 'promo única' — son frases que disparan los filtros anti-spam de WhatsApp. Hay un 40% de probabilidad de que tu cuenta reciba reportes.",
                        suggestion:
                            "Reformulá las garantías. En vez de 'garantizada' decí 'durante esta semana'. En vez de '100% gratis' decí 'sin costo de envío'.",
                    },
                    {
                        category: "campaign_clash",
                        severity: "warning",
                        message:
                            "Tu campaña 'Despertar inactivos' (always-on) ya está apuntando al mismo segmento. Si activás esta, los usuarios reciben 2 mensajes en la misma semana.",
                        suggestion:
                            "Pausá temporalmente 'Despertar inactivos' o cambiá esta a un subsegmento (ej: solo Belgrano).",
                    },
                ],
            },
            createdBy: "demo-user",
            createdAt: hoursAgo(5),
            updatedAt: hoursAgo(2),
        },
        // Pending review — recién enviada, la IA todavía analizando
        {
            id: cusId3,
            brandId: "brand-demo",
            name: "Almuerzos de oficina · Lunes a viernes",
            emoji: "🍱",
            preset: "custom",
            origin: "custom",
            segmentId: segId4,
            cadence: {
                kind: "recurring",
                sendWindow: { start: "10:30", end: "12:30" },
                daysOfWeek: [1, 2, 3, 4, 5],
            },
            status: "pending_review",
            metrics: {
                runsCount: 0,
                peopleReached: 0,
                conversions: 0,
                revenueGeneratedUSD: 0,
            },
            customGoal:
                "Captar el segmento de oficina a la hora del almuerzo con un menú ejecutivo de 25 min de entrega.",
            customMetrics: ["orders_count", "new_customers"],
            messagePreview:
                "Hola {{nombre}} 🍱 ¿Almuerzo a la oficina? Menú ejecutivo en 25 min, sin costo de envío de lunes a viernes entre 11:30 y 13:30.",
            aiReview: {
                iteration: 1,
                lastReviewedAt: undefined,
                observations: [],
            },
            createdBy: "demo-user",
            createdAt: minutesAgo(4),
            updatedAt: minutesAgo(4),
        },
    ];

    // Concat al final del array de objectives
    objectives.push(...suggestedCampaigns, ...customInReview);

    /* ───────────────────────────────────────────────────────────────
     *  Enriquecimiento multi-canal + triggers para 4-5 campañas demo
     * ───────────────────────────────────────────────────────────────
     *
     *  Hace que algunas campañas existentes tengan canales activos +
     *  contenido por canal + triggers configurados. Así el feed muestra
     *  los chips de canales y el trigger count desde el día 1.
     */

    const tid = () => "trg-" + Math.random().toString(36).slice(2, 10);

    // 1. Cumpleaños del cliente — Email + WhatsApp + Push, trigger birthday
    const birthdayCampaign = objectives.find(
        (o: any) => o.preset === "birthday",
    ) as any;
    if (birthdayCampaign) {
        birthdayCampaign.channels = ["email", "whatsapp", "push"];
        birthdayCampaign.content = {
            email: {
                subject: "🎂 ¡Feliz cumpleaños, {{nombre}}!",
                preheader:
                    "Tu postre favorito está de regalo esta semana, vení a celebrar",
                body: "Hola {{nombre}},\n\n¡Hoy es tu cumple! Para festejar te regalamos un postre + 15% off válido los próximos 7 días en cualquiera de nuestras sucursales.\n\nCódigo: BDAY{{año}}\n\nFeliz día,\nEl equipo",
            },
            whatsapp: {
                text: "Hola {{nombre}} 🎂 ¡Feliz cumple! Te regalamos un postre + 15% off válido toda la semana. Codigo: BDAY{{año}} 🎉",
            },
            push: {
                title: "🎂 ¡Feliz cumpleaños!",
                body: "Tenés un postre de regalo + 15% off esta semana. Veni a celebrar.",
                deepLink: "deenex://promociones/birthday",
            },
        };
        birthdayCampaign.triggers = {
            logic: "AND",
            items: [
                {
                    id: tid(),
                    type: "event",
                    eventKey: "birthday",
                },
            ],
        };
    }

    // 2. Carrito abandonado — WhatsApp + Push, trigger cart_abandoned 2h AND value > 5000
    const cartCampaign = objectives.find(
        (o: any) => o.preset === "cart_abandon",
    ) as any;
    if (cartCampaign) {
        cartCampaign.channels = ["whatsapp", "push"];
        cartCampaign.content = {
            whatsapp: {
                text: "Hola {{nombre}} 🛒 Te quedó el carrito esperando — completá tu pedido en 1 click y te regalamos envío gratis hoy.",
            },
            push: {
                title: "Tu pedido está esperando 🛒",
                body: "Cerralo ahora y el envío sale gratis",
                deepLink: "deenex://carrito",
            },
        };
        cartCampaign.triggers = {
            logic: "AND",
            items: [
                {
                    id: tid(),
                    type: "event",
                    eventKey: "cart_abandoned",
                    params: { hours: 2, minValue: 5000 },
                },
            ],
        };
    }

    // 3. NUEVA — Manejo de quejas por WhatsApp
    const wa_complaint_id = id("obj-");
    const waComplaintCampaign: any = {
        id: wa_complaint_id,
        brandId: "brand-demo",
        name: "Atender quejas por WhatsApp",
        emoji: "😤",
        preset: "custom",
        origin: "automatic" as const,
        isAlwaysOn: true,
        segmentId: undefined,
        cadence: {
            kind: "recurring",
            sendWindow: { start: "00:00", end: "23:59" },
            daysOfWeek: [1, 2, 3, 4, 5, 6, 7],
        },
        status: "active",
        metrics: {
            runsCount: 23,
            peopleReached: 23,
            conversions: 17,
            revenueGeneratedUSD: 0,
            lastRunAt: hoursAgo(3),
        },
        channels: ["whatsapp"] as const,
        content: {
            whatsapp: {
                text: "Hola {{nombre}}, lamentamos que hayas tenido una mala experiencia. Vamos a revisar tu caso ahora mismo y te respondemos en un máximo de 30 minutos. Gracias por avisarnos 🙏",
            },
        },
        triggers: {
            logic: "AND" as const,
            items: [
                {
                    id: tid(),
                    type: "whatsapp_message" as const,
                    category: "complaint" as const,
                    keywords: ["queja", "horrible", "pésimo", "frío"],
                },
            ],
        },
        aiReview: {
            iteration: 1,
            lastReviewedAt: daysAgo(10),
            verdict: "approved" as const,
            observations: [],
            approvedAt: daysAgo(10),
            approverNote:
                "Always-on automática. Responde en menos de 30 segundos a quejas detectadas.",
        },
        createdBy: "ai-deenex",
        createdAt: daysAgo(45),
        updatedAt: hoursAgo(3),
    };
    objectives.push(waComplaintCampaign);

    // 4. Reactivar VIPs — Email + WhatsApp, trigger inactive_days + vip
    const winbackVip = objectives.find(
        (o: any) => o.preset === "winback_vip" && o.status === "active",
    ) as any;
    if (winbackVip) {
        winbackVip.channels = ["email", "whatsapp"];
        winbackVip.content = {
            email: {
                subject: "Te extrañamos, {{nombre}}",
                preheader:
                    "Tu mesa favorita te espera con un beneficio personal",
                body: "Hola {{nombre}},\n\nHace tiempo que no nos vemos y queremos invitarte a volver. Sos uno de nuestros clientes más fieles y queremos retribuir esa lealtad.\n\n• 25% off en tu próximo pedido\n• Postre cortesía para 2 personas\n• Válido los próximos 14 días\n\nTe estamos esperando.",
            },
            whatsapp: {
                text: "Hola {{nombre}} 💜 Hace casi un mes que no pasás. Te dejamos 25% off + postre cortesía válido los próximos 14 días. Te esperamos.",
            },
        };
        winbackVip.triggers = {
            logic: "AND",
            items: [
                {
                    id: tid(),
                    type: "event",
                    eventKey: "inactive_days",
                    params: { days: 30 },
                },
                {
                    id: tid(),
                    type: "event",
                    eventKey: "vip_upgrade",
                },
            ],
        };
    }

    // 5. NUEVA — Lanzamiento de producto programado (fecha fija + recurrente)
    const launch_id = id("obj-");
    const launchCampaign: any = {
        id: launch_id,
        brandId: "brand-demo",
        name: "Lanzamiento: Edición limitada invierno",
        emoji: "❄️",
        preset: "custom",
        origin: "custom" as const,
        segmentId: undefined,
        cadence: {
            kind: "one_shot",
            sendWindow: { start: "11:00", end: "20:00" },
            scheduledFor: daysAgo(-12),
        },
        status: "active",
        metrics: {
            runsCount: 0,
            peopleReached: 0,
            conversions: 0,
            revenueGeneratedUSD: 0,
        },
        customGoal:
            "Avisar a toda la base sobre el lanzamiento de la nueva edición limitada de invierno, programado para el 7 de junio.",
        customMetrics: ["new_customers", "revenue"] as const,
        channels: ["email", "push"] as const,
        content: {
            email: {
                subject: "❄️ Llegó la edición limitada de invierno",
                preheader: "3 platos exclusivos, solo por 30 días",
                body: "Hola {{nombre}},\n\nDespués de meses de prueba, lanzamos nuestra Edición Limitada de Invierno con 3 platos exclusivos diseñados para los días fríos.\n\nDesde el 7 de junio, solo por 30 días. Reservá tu mesa con anticipación.\n\nVer la carta completa →",
            },
            push: {
                title: "❄️ Edición invierno · Solo 30 días",
                body: "3 platos exclusivos disponibles desde el 7 de junio. Reservá ya tu mesa.",
                deepLink: "deenex://lanzamientos/invierno",
            },
        },
        triggers: {
            logic: "OR" as const,
            items: [
                {
                    id: tid(),
                    type: "time" as const,
                    mode: "fixed" as const,
                    scheduledFor: daysAgo(-12),
                },
                {
                    id: tid(),
                    type: "event" as const,
                    eventKey: "high_intent_browse",
                },
            ],
        },
        aiReview: {
            iteration: 1,
            lastReviewedAt: hoursAgo(48),
            verdict: "approved" as const,
            observations: [],
            approvedAt: hoursAgo(48),
            approverNote:
                "Programada para el 7 de junio. Email + push pre-aprobados. También dispara para usuarios que vean los nuevos platos.",
        },
        createdBy: "demo-user",
        createdAt: daysAgo(5),
        updatedAt: hoursAgo(48),
    };
    objectives.push(launchCampaign);

    /* ─── Notificaciones ─── */
    const notifications = [
        {
            id: id("notif-"),
            brandId: "brand-demo",
            segmentId: segId1,
            segmentName: "VIP recurrentes",
            title: "Ofertón sábado",
            body: "Hasta 40% off en toda la tienda este sábado. Entrá ya",
            status: "sent",
            sentAt: daysAgo(1),
            recipientCount: 1838,
            metrics: { delivered: 1773, opened: 612, clicked: 73, failed: 65 },
            createdBy: "demo-user",
            createdAt: daysAgo(1),
            updatedAt: daysAgo(1),
        },
        {
            id: id("notif-"),
            brandId: "brand-demo",
            segmentId: segId2,
            segmentName: "Carritos abandonados",
            title: "Carrito esperando",
            body: "Te dejaste 2 productos en el carrito ¿completas la compra?",
            status: "sent",
            sentAt: daysAgo(3),
            recipientCount: 636,
            metrics: { delivered: 612, opened: 215, clicked: 18, failed: 24 },
            createdBy: "demo-user",
            createdAt: daysAgo(3),
            updatedAt: daysAgo(3),
        },
        {
            id: id("notif-"),
            brandId: "brand-demo",
            segmentId: segId3,
            segmentName: "Inactivos 30d",
            title: "Volvé a vernos",
            body:
                "Hace 30 días que no te pasás. Te dejamos 15% para tu próximo pedido",
            status: "sent",
            sentAt: daysAgo(5),
            recipientCount: 574,
            metrics: { delivered: 548, opened: 99, clicked: 6, failed: 26 },
            createdBy: "demo-user",
            createdAt: daysAgo(5),
            updatedAt: daysAgo(5),
        },
        {
            id: id("notif-"),
            brandId: "brand-demo",
            segmentId: segId1,
            segmentName: "Cumpleañeros del mes",
            title: "Feliz cumpleaños",
            body:
                "Hoy te regalamos un descuento especial 🎂 entrá y descubrilo",
            status: "sent",
            sentAt: daysAgo(7),
            recipientCount: 821,
            metrics: { delivered: 805, opened: 320, clicked: 42, failed: 16 },
            createdBy: "demo-user",
            createdAt: daysAgo(7),
            updatedAt: daysAgo(7),
        },
    ];

    /* ─── Campañas WhatsApp ─── */
    const whatsappCampaigns = [
        {
            id: id("wac-"),
            brandId: "brand-demo",
            segmentId: segId1,
            segmentName: "Clientes VIP",
            templateId: tplId3,
            templateName: "reactivacion_inactivos",
            bodyVariables: { "1": "Juan", "2": "15" },
            status: "sent",
            sentAt: daysAgo(2),
            recipientCount: 936,
            estimatedCostUSD: 53.63,
            metrics: { delivered: 915, read: 712, replied: 142, failed: 21 },
            createdBy: "demo-user",
            createdAt: daysAgo(2),
            updatedAt: daysAgo(2),
        },
        {
            id: id("wac-"),
            brandId: "brand-demo",
            segmentId: segId2,
            segmentName: "Carritos abandonados",
            templateId: tplId2,
            templateName: "carrito_abandonado",
            bodyVariables: { "1": "María", "2": "2", "3": "4500" },
            status: "sent",
            sentAt: daysAgo(4),
            recipientCount: 218,
            estimatedCostUSD: 12.49,
            metrics: { delivered: 212, read: 178, replied: 34, failed: 6 },
            createdBy: "demo-user",
            createdAt: daysAgo(4),
            updatedAt: daysAgo(4),
        },
    ];

    /* ─── Brand Rules ─── */
    const brandRules = {
        id: id("rules-"),
        brandId: "brand-demo",
        tone: 65,
        aggressiveness: 35,
        forbiddenWords: ["guaranteed", "100% safe", "risk-free"],
        sendWindow: { start: "10:00", end: "21:00" },
        maxBudgetUSD: 500,
        frequencyCap: "1_week",
        minRoi: 3.0,
        fairnessCap: 20,
        updatedAt: daysAgo(3),
        updatedBy: "demo-user",
    };

    /* ─── WhatsApp Accounts (vinculadas vía QR) ───
     * 4 cuentas que muestran los 4 escenarios de activación reales:
     *  - Corporativo: GLOBAL, ACTIVE (45 días, sin restricciones)
     *  - Palermo:     LOCAL, WARMING_UP (día 8/14, límite 500/día), default
     *  - Belgrano:    LOCAL, PAUSED (auto por health yellow + reportes spam)
     *  - Recoleta:    LOCAL, PENDING_QR → cuando se vincule → UNCONFIGURED
     */

    /** Setup completo helper */
    const fullSetup = {
        businessProfile: true,
        testMessage: true,
        webhookConfigured: true,
        autoReplyConfigured: true,
        objectivesAssigned: true,
        warmupAcknowledged: true,
    };

    /** Business profile common a Palermo / Belgrano / Corporativo */
    const bisteccaCorpProfile = {
        displayName: "La Bistecca",
        category: "restaurant",
        description: "Pizza, pastas y delivery en CABA",
        hours: "L-V 11:00-23:30 · S-D 12:00-24:00",
        website: "https://labistecca.com.ar",
        address: "Av. Santa Fe 4200, CABA",
    };

    const defaultAutoReply = {
        greetingEnabled: true,
        greetingMessage: "¡Hola! Soy el asistente de La Bistecca 🍝 Te respondo en un toque.",
        awayEnabled: true,
        awayMessage: "Estamos cerrados. Volvemos mañana a las 10 hs. Para urgencias: 0810-LABIS.",
        businessHoursStart: "10:00",
        businessHoursEnd: "23:30",
    };

    const whatsappAccounts = [
        {
            id: id("wa-"),
            brandId: "brand-demo",
            phoneNumber: "+54 9 11 5555-0001",
            displayName: "La Bistecca Corporativo",
            scope: "global",
            status: "connected",
            activationStatus: "active",
            setupProgress: fullSetup,
            businessProfile: { ...bisteccaCorpProfile, displayName: "La Bistecca (Corporativo)" },
            autoReply: defaultAutoReply,
            assignedObjectives: [objId1, objId2],
            isDefault: false,
            connectedAt: daysAgo(45),
            lastActivityAt: hoursAgo(0.5),
            messagesToday: 18,
            messagesLast30d: 412,
            health: {
                rating: "green",
                score: 94,
                metrics: {
                    deliveryRate: 98,
                    readRate: 84,
                    replyRate: 31,
                    blockRate: 0.2,
                    spamReports: 0,
                    uptimePercent: 99.9,
                },
                throughput: { current: 4, recommended: 25 },
                warnings: [],
                dailyTier: 10000,
                usedToday: 18,
            },
        },
        {
            id: id("wa-"),
            brandId: "brand-demo",
            phoneNumber: "+54 9 11 5555-1010",
            displayName: "La Bistecca Palermo",
            scope: "local",
            localId: "loc-1",
            status: "connected",
            activationStatus: "warming_up",
            warmupPlan: {
                currentDay: 8,
                totalDays: 14,
                dailyLimit: 500,
                nextIncreaseAt: { day: 15, newLimit: -1 },
                startedAt: daysAgo(7),
                usedToday: 47,
            },
            setupProgress: fullSetup,
            businessProfile: {
                ...bisteccaCorpProfile,
                displayName: "La Bistecca Palermo",
                address: "Av. Santa Fe 4200, Palermo, CABA",
            },
            autoReply: defaultAutoReply,
            assignedObjectives: [objId1],
            isDefault: true,
            connectedAt: daysAgo(7),
            lastActivityAt: hoursAgo(1),
            messagesToday: 47,
            messagesLast30d: 1284,
            health: {
                rating: "green",
                score: 88,
                metrics: {
                    deliveryRate: 96,
                    readRate: 78,
                    replyRate: 22,
                    blockRate: 0.8,
                    spamReports: 1,
                    uptimePercent: 98.4,
                },
                throughput: { current: 12, recommended: 25 },
                warnings: [],
                dailyTier: 500,
                usedToday: 47,
            },
        },
        {
            id: id("wa-"),
            brandId: "brand-demo",
            phoneNumber: "+54 9 11 5555-2020",
            displayName: "La Bistecca Belgrano",
            scope: "local",
            localId: "loc-2",
            status: "connected",
            activationStatus: "paused",
            setupProgress: fullSetup,
            businessProfile: {
                ...bisteccaCorpProfile,
                displayName: "La Bistecca Belgrano",
                address: "Cabildo 2100, Belgrano, CABA",
            },
            autoReply: defaultAutoReply,
            assignedObjectives: [],
            isDefault: false,
            connectedAt: daysAgo(8),
            lastActivityAt: hoursAgo(2),
            messagesToday: 0,
            messagesLast30d: 893,
            health: {
                rating: "yellow",
                score: 62,
                metrics: {
                    deliveryRate: 87,
                    readRate: 61,
                    replyRate: 14,
                    blockRate: 3.4,
                    spamReports: 7,
                    uptimePercent: 71.2,
                },
                throughput: { current: 0, recommended: 25 },
                warnings: [
                    "Auto-pausada: tasa de bloqueo alta (3.4%)",
                    "7 reportes de spam los últimos 7 días",
                    "Revisar campañas activas antes de reactivar",
                ],
                dailyTier: 1000,
                usedToday: 0,
            },
        },
        {
            id: id("wa-"),
            brandId: "brand-demo",
            phoneNumber: "+54 9 11 5555-3030",
            displayName: "La Bistecca Recoleta",
            scope: "local",
            localId: "loc-3",
            status: "pending_qr",
            activationStatus: "unconfigured",
            isDefault: false,
            messagesToday: 0,
            messagesLast30d: 0,
            // sin setupProgress / businessProfile / health hasta que se conecte
        },
    ];

    /* ─── Inbox notifications (eventos del sistema al admin) ─── */
    const inboxNotifications: InboxNotification[] = [
        // ─── Eventos del modelo de 3 tipos de campaña (recientes) ───
        {
            id: id("inb-"),
            type: "campaign_needs_changes",
            severity: "warning",
            title: "Tu campaña necesita ajustes",
            message:
                "'Promo agresiva 50% off · Inactivos' tiene 3 observaciones de la IA. Revisalas y reenviá para aprobación.",
            link: "/objectives?filter=needs_changes",
            createdAt: minutesAgo(2),
            read: false,
        },
        {
            id: id("inb-"),
            type: "campaign_suggested",
            severity: "info",
            title: "Nueva sugerencia: Día del Padre",
            message:
                "Falta menos de 1 mes. Restaurantes similares duplican reservas activando esta campaña con 3 semanas de anticipación. Aceptala o editala.",
            link: "/objectives?filter=suggested",
            createdAt: hoursAgo(8),
            read: false,
        },
        {
            id: id("inb-"),
            type: "campaign_suggested",
            severity: "info",
            title: "Nueva sugerencia: 38 VIPs enfriándose",
            message:
                "Detecté 38 clientes VIP a punto de cruzar los 30 días sin pedir. Si los reactivás ahora, evitás que pasen al pool de inactivos genéricos.",
            link: "/objectives?filter=suggested",
            createdAt: hoursAgo(14),
            read: false,
        },
        {
            id: id("inb-"),
            type: "ban_risk",
            severity: "critical",
            title: "Riesgo de ban detectado",
            message:
                "La cuenta WhatsApp 'Centro' recibió 3 reportes de spam en las últimas 24h. Recomendamos pausar campañas masivas.",
            link: "/whatsapp/accounts",
            createdAt: minutesAgo(8),
            read: false,
        },
        {
            id: id("inb-"),
            type: "ai_suggestion",
            severity: "info",
            title: "Sugerencia de la IA",
            message:
                "Tu tono actual es 65 (Neutral). Subirlo a 75 (Amigable) podría mejorar engagement +12% según campañas similares.",
            link: "/brand?tab=personality",
            createdAt: minutesAgo(35),
            read: false,
        },
        {
            id: id("inb-"),
            type: "campaign_sent",
            severity: "success",
            title: "Campaña enviada con éxito",
            message:
                "'Carrito abandonado · Recordatorio 24h' alcanzó 234 usuarios. Tasa de apertura: 68%.",
            link: "/objectives",
            createdAt: minutesAgo(95),
            read: false,
        },
        {
            id: id("inb-"),
            type: "word_blocked",
            severity: "warning",
            title: "3 mensajes bloqueados",
            message:
                "La IA bloqueó 3 mensajes que contenían la palabra prohibida '100% seguro'. Revisá tus reglas.",
            link: "/brand?tab=security",
            createdAt: minutesAgo(180),
            read: false,
        },
        {
            id: id("inb-"),
            type: "segment_growing",
            severity: "success",
            title: "Segmento VIPs creció",
            message:
                "El segmento 'VIPs frecuentes' pasó de 156 a 175 usuarios esta semana (+12%).",
            link: "/segments",
            createdAt: minutesAgo(360),
            read: true,
        },
        {
            id: id("inb-"),
            type: "whatsapp_warmup",
            severity: "info",
            title: "Warmup avanzó a fase 3",
            message:
                "La cuenta 'Madero' completó la fase 2 del warmup. Ahora puede enviar hasta 250 mensajes/día.",
            link: "/whatsapp/accounts",
            createdAt: hoursAgo(14),
            read: true,
        },
        {
            id: id("inb-"),
            type: "frequency_cap",
            severity: "warning",
            title: "Frecuencia tope alcanzada",
            message:
                "47 usuarios ya recibieron el máximo de mensajes esta semana. Quedan excluidos de próximas campañas hasta el lunes.",
            createdAt: hoursAgo(22),
            read: true,
        },
        {
            id: id("inb-"),
            type: "offhours_postponed",
            severity: "info",
            title: "Envíos pospuestos por horario",
            message:
                "Se pospusieron 12 mensajes que iban a salir a las 23:40 (fuera de tu ventana 10:00–21:00). Saldrán mañana a las 10:00.",
            link: "/brand?tab=security",
            createdAt: daysAgo(1),
            read: true,
        },
        {
            id: id("inb-"),
            type: "template_approved",
            severity: "success",
            title: "Plantilla aprobada",
            message:
                "Tu plantilla 'Saludo cumpleaños · Versión 2' fue aprobada y está lista para usar.",
            link: "/whatsapp/templates",
            createdAt: daysAgo(2),
            read: true,
        },
        {
            id: id("inb-"),
            type: "system_update",
            severity: "info",
            title: "Novedades en /brand",
            message:
                "Rediseñamos el Manual de tu IA con tabs y agregamos un probador en vivo con 18 escenarios.",
            link: "/brand",
            createdAt: daysAgo(3),
            read: true,
        },
    ];

    return {
        objectives,
        segments,
        templates,
        notifications,
        whatsappCampaigns,
        whatsappAccounts,
        brandRules,
        currentUser: null,
        inboxNotifications,
        assistantHistory: [],
    };
}
